import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * useTouchGestures - Custom hook for handling touch gestures
 *
 * Features:
 * - Swipe detection (left/right/up/down)
 * - Long press detection
 * - Touch ripple effect coordinates
 *
 * @param {Object} options - Configuration options
 * @param {number} options.swipeThreshold - Min distance for swipe detection (default: 50px)
 * @param {number} options.swipeVelocityThreshold - Min velocity for swipe (default: 0.3 px/ms)
 * @param {number} options.longPressDelay - Delay for long press (default: 500ms)
 * @param {Function} options.onSwipeLeft - Callback for left swipe
 * @param {Function} options.onSwipeRight - Callback for right swipe
 * @param {Function} options.onSwipeUp - Callback for up swipe
 * @param {Function} options.onSwipeDown - Callback for down swipe
 * @param {Function} options.onLongPress - Callback for long press
 * @returns {Object} Touch event handlers and state
 */
export function useTouchGestures(options = {}) {
  const {
    swipeThreshold = 50,
    swipeVelocityThreshold = 0.3,
    longPressDelay = 500,
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    onLongPress,
  } = options;

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isLongPress, setIsLongPress] = useState(false);
  const [longPressCoords, setLongPressCoords] = useState(null);

  const longPressTimerRef = useRef(null);
  const touchStartTimeRef = useRef(null);
  const hasMovedRef = useRef(false);

  // Clean up long press timer
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  const handleTouchStart = useCallback((e) => {
    // Don't interfere with scrollable elements
    if (e.target.closest('[data-no-swipe]')) return;

    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
    setTouchEnd(null);
    touchStartTimeRef.current = Date.now();
    hasMovedRef.current = false;
    setIsLongPress(false);
    setLongPressCoords(null);

    // Start long press timer
    if (onLongPress) {
      longPressTimerRef.current = setTimeout(() => {
        if (!hasMovedRef.current) {
          setIsLongPress(true);
          setLongPressCoords({ x: touch.clientX, y: touch.clientY });

          // Vibrate if supported (haptic feedback)
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }

          onLongPress({ x: touch.clientX, y: touch.clientY }, e);
        }
      }, longPressDelay);
    }
  }, [longPressDelay, onLongPress]);

  const handleTouchMove = useCallback((e) => {
    if (!touchStart) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStart.x);
    const deltaY = Math.abs(touch.clientY - touchStart.y);

    // If moved more than 10px, cancel long press
    if (deltaX > 10 || deltaY > 10) {
      hasMovedRef.current = true;
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }

    setTouchEnd({ x: touch.clientX, y: touch.clientY });
  }, [touchStart]);

  const handleTouchEnd = useCallback((e) => {
    // Clear long press timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    // If it was a long press, don't process as swipe
    if (isLongPress) {
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }

    if (!touchStart || !touchEnd) {
      setTouchStart(null);
      return;
    }

    const deltaX = touchEnd.x - touchStart.x;
    const deltaY = touchEnd.y - touchStart.y;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Calculate velocity
    const touchDuration = Date.now() - touchStartTimeRef.current;
    const velocityX = absDeltaX / touchDuration;
    const velocityY = absDeltaY / touchDuration;

    // Determine if it's a horizontal or vertical swipe
    const isHorizontalSwipe = absDeltaX > absDeltaY;

    if (isHorizontalSwipe && absDeltaX > swipeThreshold && velocityX > swipeVelocityThreshold) {
      // Horizontal swipe
      if (deltaX > 0 && onSwipeRight) {
        onSwipeRight();
      } else if (deltaX < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
    } else if (!isHorizontalSwipe && absDeltaY > swipeThreshold && velocityY > swipeVelocityThreshold) {
      // Vertical swipe
      if (deltaY > 0 && onSwipeDown) {
        onSwipeDown();
      } else if (deltaY < 0 && onSwipeUp) {
        onSwipeUp();
      }
    }

    // Reset state
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, isLongPress, swipeThreshold, swipeVelocityThreshold, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown]);

  const handleTouchCancel = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setTouchStart(null);
    setTouchEnd(null);
    setIsLongPress(false);
    setLongPressCoords(null);
  }, []);

  return {
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: handleTouchCancel,
    },
    isLongPress,
    longPressCoords,
    isSwiping: touchStart !== null && touchEnd !== null,
  };
}

/**
 * useSwipeToReveal - Hook for swipe-to-reveal sidebar pattern
 *
 * @param {Object} options - Configuration
 * @param {boolean} options.enabled - Whether swipe is enabled
 * @param {number} options.edgeWidth - Width of edge trigger zone (default: 20px)
 * @param {Function} options.onReveal - Callback when sidebar should be revealed
 * @param {Function} options.onHide - Callback when sidebar should be hidden
 */
export function useSwipeToReveal(options = {}) {
  const {
    enabled = true,
    edgeWidth = 20,
    onReveal,
    onHide,
  } = options;

  const touchStartRef = useRef(null);
  const isEdgeSwipeRef = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;

    const touch = e.touches[0];
    const startX = touch.clientX;

    // Check if touch started from left edge
    isEdgeSwipeRef.current = startX <= edgeWidth;
    touchStartRef.current = { x: startX, y: touch.clientY };
  }, [enabled, edgeWidth]);

  const handleTouchEnd = useCallback((e) => {
    if (!enabled || !touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

    // Require horizontal swipe (deltaX > deltaY) and minimum distance
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > deltaY) {
      if (deltaX > 0 && isEdgeSwipeRef.current && onReveal) {
        // Swipe right from edge - reveal sidebar
        onReveal();
      } else if (deltaX < 0 && onHide) {
        // Swipe left - hide sidebar
        onHide();
      }
    }

    touchStartRef.current = null;
    isEdgeSwipeRef.current = false;
  }, [enabled, onReveal, onHide]);

  return {
    swipeHandlers: {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
    },
  };
}

/**
 * useLongPress - Simple long press hook
 *
 * @param {Function} callback - Function to call on long press
 * @param {number} delay - Delay in ms (default: 500)
 * @returns {Object} Event handlers for long press
 */
export function useLongPress(callback, delay = 500) {
  const timerRef = useRef(null);
  const isLongPressRef = useRef(false);
  const targetRef = useRef(null);

  const start = useCallback((e) => {
    // Prevent text selection on long press
    e.target.style.userSelect = 'none';
    e.target.style.webkitUserSelect = 'none';

    targetRef.current = e.target;
    isLongPressRef.current = false;

    const coords = e.touches
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
      : { x: e.clientX, y: e.clientY };

    timerRef.current = setTimeout(() => {
      isLongPressRef.current = true;

      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }

      callback?.(coords, e);
    }, delay);
  }, [callback, delay]);

  const clear = useCallback((e, shouldTriggerClick = true) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Restore text selection
    if (targetRef.current) {
      targetRef.current.style.userSelect = '';
      targetRef.current.style.webkitUserSelect = '';
    }

    // Prevent click if it was a long press
    if (isLongPressRef.current && e) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return {
    onTouchStart: start,
    onTouchEnd: clear,
    onTouchMove: (e) => {
      // Cancel if moved too much
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    onTouchCancel: clear,
    onMouseDown: start,
    onMouseUp: clear,
    onMouseLeave: clear,
  };
}

export default useTouchGestures;
